
import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../../store';
import { createCustomForm, updateCustomForm } from '../../../../../modules/masterdata/actions';
import {
  selectCustomFormLoading,
  selectCustomFormSuccess
} from '../../../../../modules/masterdata/selectors';

import { CustomForm } from '../../../../../modules/masterdata/types';

interface CreateCustomFormModalProps {
  open: boolean;
  onClose: () => void;
  editData?: CustomForm | null;
}

const CreateCustomFormModal: React.FC<CreateCustomFormModalProps> = ({ open, onClose, editData }) => {
  const dispatch = useDispatch<AppDispatch>();
  const loading = useSelector(selectCustomFormLoading);
  const success = useSelector(selectCustomFormSuccess);

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [order, setOrder] = useState<number>(0);
  const [errors, setErrors] = useState<{ formName?: string; formDescription?: string; order?: string }>({});

  useEffect(() => {
    if (editData) {
      setFormName(editData.form_name);
      setFormDescription(editData.form_description);
      setOrder(editData.order || 0);
    } else {
      setFormName('');
      setFormDescription('');
      setOrder(0);
    }
  }, [editData, open]);

  const handleClose = useCallback(() => {
    setFormName('');
    setFormDescription('');
    setOrder(0);
    setErrors({});
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (success) {
      handleClose();
    }
  }, [success, handleClose]);

  const validate = () => {
    const newErrors: { formName?: string; formDescription?: string; order?: string } = {};
    if (!formName.trim()) newErrors.formName = 'Form Name is required';
    if (!formDescription.trim()) newErrors.formDescription = 'Description is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      if (editData && editData.form_id) {
        dispatch(updateCustomForm({
          form_id: editData.form_id,
          form_name: formName,
          form_description: formDescription,
          order: Number(order)
        }));
      } else {
        dispatch(createCustomForm({
          form_name: formName,
          form_description: formDescription,
          order: Number(order)
        }));
      }
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{editData ? 'Edit Custom Form' : 'Create Custom Form'}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Form Name"
            variant="outlined"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            error={!!errors.formName}
            helperText={errors.formName}
          />

          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            rows={4}
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            error={!!errors.formDescription}
            helperText={errors.formDescription}
          />

          <TextField
            fullWidth
            label="Order"
            variant="outlined"
            type="number"
            value={order}
            onChange={(e) => setOrder(Number(e.target.value))}
            error={!!errors.order}
            helperText={errors.order}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ bgcolor: '#5E8E7E', '&:hover': { bgcolor: '#4A7A6A' } }}
        >
          {loading ? (editData ? 'Updating...' : 'Creating...') : (editData ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCustomFormModal;
